import {Component, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {IoniconsOutline, NavItem, PtUiAppRoutes} from '@pt/pt-models';
import {NgxPermissionsService} from 'ngx-permissions';
import {Observable, Subject} from 'rxjs';
import {LegalEntity} from '@ms-organization-entities';
import {FormControl, FormGroup} from '@angular/forms';
import {distinctUntilChanged, filter, map, switchMap, takeUntil} from 'rxjs/operators';
import {NgrxCoreReducer, PtCoreActions, PtCoreSelectors} from '@pt/pt-core';
import {Store} from '@ngrx/store';
import {NavigationEnd, Router} from '@angular/router';

@Component({
  selector: 'pt-navbar-legal-entity-item',
  template: `
    <div
      class="legal-entity-container"
      [ngClass]="{ 'legal-entity-container--disabled': !isEnabled }"
    >
      <form [formGroup]="formGroup" class="legal-entity-form">
        <ng-select
          [id]="'legalEntity'"
          class="legal-entity-select"
          [items]="internalLegalEntity$ | async"
          [closeOnSelect]="false"
          [clearSearchOnAdd]="true"
          [multiple]="true"
          bindLabel="name"
          appendTo="body"
          formControlName="legalEntity"
          [readonly]="!isEnabled || (item?.disabled | async)"
          [placeholder]="'PLACEHOLDERS.SELECT_LEGAL_ENTITY' | translate"
        >
          <ng-template ng-header-tmp>
            <div class="px-1">
              <!--suppress TypeScriptUnresolvedVariable -->
              <input
                type="checkbox"
                id="selectAll"
                class="cursor-pointer"
                [checked]="selectedAll"
                (click)="toggleSelectAll($event)"
              />
              <label class="m-0 ps-2 form-label" for="selectAll">{{
                'LABELS.SELECT_ALL' | translate
              }}</label>
            </div>
          </ng-template>

          <ng-template
            ng-multi-label-tmp
            let-items="items"
            let-clear="clear"
            let-index="index"
          >
            <ng-container *ngIf="!selectedAll">
              <div class="ng-value" *ngFor="let item of items | slice : 0 : 1">
                <span class="ng-value-label  ">{{ $any(item).name }}</span>
                <span
                  class="ng-value-icon right"
                  (click)="clear($any(item))"
                  aria-hidden="true"
                  >×</span
                >
              </div>

              <div class="ng-value" *ngIf="items.length > 1">
                <span class="ng-value-label"
                  >{{ items.length - 1 }}
                  <span class="text-lowercase">{{
                    'LABELS.MORE' | translate
                  }}</span
                  >...</span
                >
              </div>
            </ng-container>

            <div class="ng-value" *ngIf="selectedAll">
              <span class="ng-value-label">{{
                'LABELS.ALL_LEGAL_ENTITIES' | translate
              }}</span>
            </div>
          </ng-template>

          <ng-template
            ng-option-tmp
            let-item="item"
            let-item$="item$"
            let-index="index"
          >
            <div class="select-container-item">
              <input
                id="item-{{ index }}"
                type="checkbox"
                class="me-2"
                [checked]="isSelected($any(item), selectedLegalEntity$ | async)"
              />
              {{ $any(item).name }}
            </div>
          </ng-template>
        </ng-select>
      </form>
      <div class="reload-button">
        <button
          class="btn btn-sm btn-link m-0 p-0 bg-white"
          [disabled]="!isEnabled"
          (click)="reloadLegalEntity()"
        >
          <pt-icon [key]="IconReload"></pt-icon>
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .legal-entity-container {
        margin-left: 5px;
        margin-right: 5px;
      }

      .select-container-item {
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }

      .legal-entity-container .ng-select .ng-select-container {
        height: 30px;
        max-height: 30px;
        min-height: 30px;
      }

      .legal-entity-container .ng-value {
        display: inline-flex !important;
      }

      .legal-entity-container .ng-value-label {
        max-width: 150px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .legal-entity-container--disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .legal-entity-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-direction: row;
      }

      .legal-entity-container .reload-button {
        background: white;
        align-items: center;
        display: flex;
        border: 1px solid #ccc;
        border-left: 0;
        height: 30px;
        max-height: 30px;
        min-height: 30px;
        width: 25px;
      }

      .legal-entity-container .legal-entity-form {
        display: inline-flex;
        height: 100%;
      }

      .legal-entity-container .legal-entity-select {
        width: 300px;
      }

      .legal-entity-container .ng-select-container {
        border-radius: 0 !important;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class NavbarLegalEntityItemComponent implements OnInit, OnDestroy {
  @Input() item: NavItem;

  internalLegalEntity$: Observable<LegalEntity[]> = this.store
    .select(PtCoreSelectors.legalEntity)
    .pipe(map((arr) => arr.filter((f) => !f.external)));
  selectedLegalEntity$ = this.store.select(PtCoreSelectors.selectedLegalEntity);

  IconReload = IoniconsOutline.reload;

  legalEntity: Observable<LegalEntity[]>;
  public selectedAll = false;
  isEnabled: boolean;
  formGroup: FormGroup;
  destroy$ = new Subject<boolean>();
  private isEnabledPermmission = true;

  constructor(
    private permissionsService: NgxPermissionsService,
    private router: Router,
    private store: Store<NgrxCoreReducer.CoreState>
  ) {}

  isSelected(current: LegalEntity, all: LegalEntity[]) {
    return current && all && !!all.find((l) => l.id === current.id);
  }

  ngOnInit(): void {
    this.setEnabledIfValidUrl(this.router.url);

    // this.store.dispatch(PtCoreActions.getLegalEntity());

    this.formGroup = new FormGroup({
      legalEntity: new FormControl(),
    });

    this.legalEntity = this.item.data;

    if (this.item?.permissEnabled) {
      this.permissionsService
        .hasPermission(this.item.permissEnabled)
        .then((enabled) => {
          this.isEnabledPermmission = enabled;
        });
    }

    this.formGroup.controls.legalEntity.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((legalEntity: LegalEntity[]) => {
        this.store.dispatch(
          PtCoreActions.selectLegalEntity({ selectedLegalEntity: legalEntity })
        );
      });

    this.internalLegalEntity$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        switchMap((legalEntities) => {
          return this.selectedLegalEntity$.pipe(
            map((selectedLegalEntities) => {
              if (legalEntities && selectedLegalEntities) {
                return {
                  legalEntities: legalEntities,
                  selectedLegalEntities: selectedLegalEntities,
                };
              }
            })
          );
        })
      )
      .subscribe((res) => {
        this.selectedAll =
          res.legalEntities?.length === res.selectedLegalEntities?.length;
      });

    this.router.events
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        filter((e) => e instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.setEnabledIfValidUrl(this.router.url);
      });
  }

  reloadLegalEntity() {
    this.store.dispatch(PtCoreActions.getLegalEntity());
    this.formGroup?.controls?.legalEntity?.setValue(null);
    this.selectedAll = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  toggleSelectAll($event: Event) {
    const checked = ($event.target as HTMLInputElement).checked;
    if (checked) {
      this.internalLegalEntity$.subscribe((legalEntities) => {
        if (legalEntities) {
          this.formGroup?.controls?.legalEntity?.setValue(legalEntities);
        }
      });
    } else {
      this.formGroup?.controls?.legalEntity?.setValue(null);
    }
    this.selectedAll = checked;
  }

  private setEnabledIfValidUrl(url: string) {
    let enabled = this.isEnabledPermmission;
    if (enabled) {
      enabled =
        (url.includes(PtUiAppRoutes.BUSINESS_MANAGEMENT) &&
          !url.includes('/' + PtUiAppRoutes.EDIT + '/') &&
          !url.includes(PtUiAppRoutes.APPOINTMENTS) &&
          !url.includes(PtUiAppRoutes.AI_ASSESSMENTS) &&
          !url.includes(PtUiAppRoutes.APP_ASSESSMENTS) &&
          !url.includes(PtUiAppRoutes.COVID_ASSESSMENTS) &&
          !url.includes(PtUiAppRoutes.COVID_TEMPLATE) &&
          !url.includes(PtUiAppRoutes.CYBER_SECURITY) &&
          !url.includes(PtUiAppRoutes.ORGANIZATIONAL_FUNCTIONS)) ||
        url.includes(PtUiAppRoutes.USERS_RESOURCES);
    }
    this.isEnabled = enabled;
  }
}
